import { UIMessage, UseChatHelpers } from '@ai-sdk/react';
import { Stack } from '@mui/material';
import { PropsWithChildren, ReactNode, useEffect, useRef } from 'react';

import { UIChatMessage } from './chat.types';
import { ChatAssistantMessage } from './ChatMessage/ChatAssistantMessage';
import { ChatUserMessage } from './ChatUserMessage/ChatUserMessage';
import { suggestionListClasses } from './SuggestionList/SuggestionList.styled';

export interface ChatConversationProps {
  messages: UIChatMessage[];
  status: UseChatHelpers<UIMessage>['status'] | undefined;
}

const MESSAGE_END_ID = 'messages-end';

export const ChatConversation = ({
  messages,
  status,
  children,
}: PropsWithChildren<ChatConversationProps>): ReactNode => {
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // scrolling to end of messages when new chat has loaded
  useEffect(() => {
    setTimeout(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, 0);
  }, [messages]);

  useEffect(() => {
    if (status === 'submitted') {
      // Add padding and scroll to end when status is submitted
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [status]);

  return (
    <Stack
      spacing={6}
      paddingBottom={5}
      flex={1}
      sx={{
        [`& :has(+ :not([id="${MESSAGE_END_ID}"])) .${suggestionListClasses.root}`]: {
          display: 'none',
        },
      }}
    >
      {messages.map((message, idx, messages) => {
        const isLastMessage = idx === messages.length - 1;

        switch (message.role) {
          case 'user':
            return <ChatUserMessage key={message.id} message={message} />;
          case 'assistant': {
            return (
              <ChatAssistantMessage
                key={message.id}
                message={message}
                isLastMessage={isLastMessage}
                // we show a streaming indicator only for the last assistant message
                isStreaming={status === 'streaming'}
              />
            );
          }
          // for now we do not support other message types
          default:
            return null;
        }
      })}

      {children}

      {/* message end placeholder for scrolling */}
      <div id="messages-end" ref={messagesEndRef} />
    </Stack>
  );
};
